// frontend/src/hooks/useSoR.js
//
// Reads the SoR descriptor for a given domain from an entity's `sor` envelope.
// The backend's serializers expose `sor` as a per-domain map of {role, vendor}
// pairs (see backend/api/serializers.py and backend/core/integration/precedence.py).
//
// SoR drives edit-ability: a field whose SoR is the ERP is read-only in Manexys
// (edited in the ERP). `isErpOwned` mirrors the backend `SoRReadOnlyMixin`; the
// grid colDef decorator that consumes it (`withSorReadOnly`) lives in the grid
// layer (components/grid/sorColumns) because it also pulls in a React tooltip
// component — keeping this a pure, component-free SoR-primitives module.
// Sales-channel-owned and PLANNING (Manexys) fields stay editable. Also used for
// informational badges and the NOT_APPLICABLE branch (hide UI surfaces whose
// data does not exist for this store — e.g. B2B orders in a Shopify-only store).
//
// This is the canonical home for SoR display metadata — `VENDOR_DISPLAY_NAMES`
// (machine vendor id → brand name) lives here so the badge, the connectors
// panel and the grid read-only tooltip all resolve the same names.

// Vendor identifier → user-facing display name. Vendor codes are stable (set at
// SlotActivation time on ConnectorInstance.vendor); brand names are NOT
// translated — product names stay as the vendor brands them. Adding a vendor =
// one entry here.
export const VENDOR_DISPLAY_NAMES = {
  manexys: 'Manexys',
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
  amazon: 'Amazon',
  magento: 'Magento',
  bigcommerce: 'BigCommerce',
  exact_online: 'Exact Online',
  dynamics_365: 'Dynamics 365 BC',
  afas: 'AFAS',
  netsuite: 'NetSuite',
  sap_business_one: 'SAP Business One',
  picqer: 'Picqer',
  shipbob: 'ShipBob',
  shiphero: 'ShipHero',
  '3pl_central': '3PL Central',
};

/**
 * @param {string} vendor  Machine vendor id from a `sor` envelope cell.
 * @returns {string}  The brand display name, or the raw id when unmapped.
 */
export function vendorDisplayName(vendor) {
  return VENDOR_DISPLAY_NAMES[vendor] ?? vendor;
}

/**
 * @param {object|null|undefined} entity  Resource carrying a `sor` envelope
 *   (Item, Supplier, Location, ItemLocation). Null/undefined returns null.
 * @param {string} domain  Wire-side domain key (e.g. 'cost', 'sell_price',
 *   'item_master', 'suppliers', 'locations'). The set of valid keys per
 *   resource is the serializer's `_SOR_FIELD_MAP` on the backend side.
 * @returns {{role: string, vendor: string}|null}  The (role, vendor) pair
 *   currently authoritative for this domain, or null when the entity is
 *   missing, the entity lacks a `sor` envelope, or the domain is not
 *   covered for this resource.
 */
export function useSoR(entity, domain) {
  return entity?.sor?.[domain] ?? null;
}

/**
 * @param {{role: string}|null|undefined} sor
 * @returns {boolean}  True when the domain has no value to display for this
 *   store (the role resolves to NOT_APPLICABLE). UI surfaces gated by this
 *   should be hidden — e.g. don't render a B2B-orders tab in a Shopify-only
 *   store. This is the only UI behavior branch that depends on the role
 *   value; everything else (PLANNING vs external) is informational.
 */
export function isNotApplicable(sor) {
  return sor?.role === 'NOT_APPLICABLE';
}

/**
 * @param {{role: string}|null|undefined} sor
 * @returns {boolean}  True when the ERP is the SoR for this domain — the field
 *   is read-only in Manexys (edited in the ERP). Mirrors the backend
 *   `_domain_sor_is_erp`; sales-channel-owned / PLANNING fields return false.
 */
export function isErpOwned(sor) {
  return sor?.role === 'ERP';
}
